import { useEffect, useRef, useState } from 'react'

/**
 * Кнопка «Скопировать» рядом со значением, которое показывают один раз
 * (FR-ADM-036).
 *
 * Экран первичной настройки говорил «Скопируйте его сейчас, второй раз не
 * покажется» — и не давал ничем скопировать: сорок четыре символа ключа
 * предлагалось выделить мышью, не промахнувшись и не задев соседний текст.
 * Промах здесь стоит дорого: второго показа нет ни у ключа API, ни у ключа
 * подписи подписки, и ошибка лечится только выпуском нового.
 *
 * Общая для всех таких мест, а не своя у каждого: одинаковых мест уже три
 * (первичная настройка, ключи API, ключ подписи), и заплатка в одном из них
 * означала бы, что четвёртое заведут снова без неё.
 */
export interface CopyButtonProps {
  /** Что кладём в буфер. */
  value: string
  /**
   * Чем это назвать в подтверждении: «Ключ скопирован». Именительный падеж,
   * с большой буквы — сообщение начинается с него.
   */
  what?: string
  /** Подпись кнопки. По умолчанию — «Скопировать». */
  label?: string
}

/** Сколько держится подтверждение. Достаточно, чтобы заметить, и не больше. */
const ПОДТВЕРЖДЕНИЕ_МС = 2000

/**
 * Кладёт текст в буфер обмена.
 *
 * Современный способ (`navigator.clipboard`) работает только в защищённом
 * контексте: по https или с localhost. Портал же нередко открывают по http
 * внутри закрытого контура — ровно там, где ключ и выдают, — и там этого
 * способа просто нет. Поэтому за ним стоит старый: скрытое поле, выделение,
 * `execCommand`. Он объявлен устаревшим, но работает везде, а «устаревший
 * способ» лучше, чем «скопировать нечем».
 */
async function положитьВБуфер(текст: string): Promise<boolean> {
  try {
    if (navigator.clipboard !== undefined) {
      await navigator.clipboard.writeText(текст)
      return true
    }
  } catch {
    // Отказ современного способа — не приговор: ниже пробуем старый.
  }

  try {
    const поле = document.createElement('textarea')
    поле.value = текст
    // Вне видимой области, но в документе: невидимое поле не выделяется.
    поле.setAttribute('aria-hidden', 'true')
    поле.style.position = 'fixed'
    поле.style.opacity = '0'
    поле.style.pointerEvents = 'none'
    document.body.appendChild(поле)
    поле.select()
    const получилось = document.execCommand('copy')
    document.body.removeChild(поле)
    return получилось
  } catch {
    return false
  }
}

export function CopyButton({ value, what = 'Значение', label = 'Скопировать' }: CopyButtonProps) {
  const [исход, setИсход] = useState<'скопировано' | 'не вышло' | null>(null)
  const таймер = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Таймер снимается при размонтировании: экран настройки уходит сразу
  // после нажатия «Войти в портал», и обновлять его состояние после этого
  // некому.
  useEffect(
    () => () => {
      if (таймер.current !== null) clearTimeout(таймер.current)
    },
    [],
  )

  function скопировать(): void {
    void положитьВБуфер(value).then((получилось) => {
      setИсход(получилось ? 'скопировано' : 'не вышло')
      if (таймер.current !== null) clearTimeout(таймер.current)
      // Отказ не гаснет сам: он требует действия — выделить и скопировать
      // руками, — а успех прочитан боковым зрением за секунду (FR-ADM-021).
      if (получилось) {
        таймер.current = setTimeout(() => setИсход(null), ПОДТВЕРЖДЕНИЕ_МС)
      }
    })
  }

  return (
    <span className="копирование">
      <button type="button" className="btn btn--small" onClick={скопировать}>
        {label}
      </button>
      {/* Ответ произносится диктором и виден глазами: нажатие, после
          которого ничего не изменилось, читается как несработавшее. */}
      <span className="копирование__исход" role="status">
        {исход === 'скопировано' ? `${what} скопирован в буфер обмена` : null}
        {исход === 'не вышло'
          ? 'Браузер не дал доступ к буферу обмена. Выделите значение и скопируйте вручную.'
          : null}
      </span>
    </span>
  )
}
